import { useCallback } from "react";

import type { InputMaybe, KidsPolicyItemInput } from "@/lib/gql/__generated__/graphql";
import { CurrencyType, KidsPolicyType, type KidsPolicyItem } from "@/lib/gql/__generated__/graphql";

import type { FormOptionType } from "@/components/ui/form/_types/type";
import { getOptionNumArray } from "@/components/ui/form/_utils";

import { KIDS_POLICY_DISCOUNT_TYPE_VARIABLES } from "../../items/edit/_shared/constants/kids-policy";

import type { ForArrayType } from "../../_model/types/auto-generator-for-view-model";

type Props = {
	kidsPolicy: ForArrayType<InputMaybe<KidsPolicyItemInput[]> | undefined> | undefined;
	onSetViewModel: <K extends "kidsPolicy">(values: { key: K; value: ForArrayType<KidsPolicyItem[]> }[]) => void;
};

export const useKidsPolicy = ({ kidsPolicy, onSetViewModel }: Props) => {
	const append = useCallback(() => {
		const lastKidsPolicyItem = kidsPolicy?.value?.at(-1);

		if ((lastKidsPolicyItem?.kidsMaxAge?.value ?? 0) >= 19) {
			window.alert("子どもの年齢の設定は最大19歳までです");
			return;
		}

		const newKidsPolicyItem = {
			kidsDiscountType: {
				isEdited: true,
				value: KidsPolicyType.Ratio,
			},
			kidsMaxAge: {
				isEdited: true,
				value:
					lastKidsPolicyItem && lastKidsPolicyItem.kidsMaxAge && lastKidsPolicyItem.kidsMaxAge.value >= 0
						? lastKidsPolicyItem.kidsMaxAge.value + 1
						: 0,
			},
			ratio: {
				isEdited: true,
				value: 100,
			},
		};

		const newKidsPolicy = kidsPolicy?.value
			? {
					...kidsPolicy,
					value: [...kidsPolicy.value, newKidsPolicyItem],
				}
			: {
					hasError: false,
					isEdited: true,
					value: [newKidsPolicyItem],
				};

		onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
	}, [kidsPolicy, onSetViewModel]);

	const canRemoveKidsPolicyItem = useCallback(
		({ index }: { index: number }) => {
			const length = kidsPolicy?.value?.length ?? 0;
			const lastItemIndex = length - 1;

			return index === lastItemIndex;
		},
		[kidsPolicy],
	);
	const removeKidsPolicyByIndex = useCallback(
		({ index }: { index: number }) => {
			if (kidsPolicy?.value && kidsPolicy.value.length > 0) {
				const newKidsPolicy = {
					...kidsPolicy,
					value: kidsPolicy.value.filter((_, i) => i !== index),
				};

				onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
			}
		},
		[kidsPolicy, onSetViewModel],
	);

	const getMinAge = useCallback(
		({ index }: { index: number }) => {
			let minAge = 0;
			if (index !== 0) {
				const prevItem = kidsPolicy!.value![index - 1];
				if (prevItem && prevItem.kidsMaxAge && prevItem.kidsMaxAge.value >= 0) {
					minAge = prevItem.kidsMaxAge.value + 1;
				}
			}

			return minAge;
		},
		[kidsPolicy],
	);

	const kidsMaxAgeOptions = useCallback(
		({ index }: { index: number }) => {
			const minAge = getMinAge({ index });

			const nextItem = kidsPolicy!.value![index + 1];
			let overAge = 20;

			if (nextItem && nextItem.kidsMaxAge && nextItem.kidsMaxAge.value) {
				overAge = nextItem.kidsMaxAge.value;
			}

			const length = Math.abs(overAge - minAge);

			return getOptionNumArray(minAge, 1, length);
		},
		[getMinAge, kidsPolicy],
	);

	const updateMaxAge = useCallback(
		({ maxAge, index }: { maxAge: number; index: number }) => {
			const newKidsPolicy = {
				hasError: false,
				isEdited: true,
				value: kidsPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							...item,
							kidsMaxAge: {
								isEdited: true,
								value: maxAge,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
		},
		[kidsPolicy, onSetViewModel],
	);

	const discountTypeOptions: FormOptionType[] = KIDS_POLICY_DISCOUNT_TYPE_VARIABLES.map((item) => {
		return {
			id: item.value,
			label: item.key,
			value: item.value,
		};
	});

	const updateDiscountType = useCallback(
		({ discountType, index }: { discountType: KidsPolicyType; index: number }) => {
			const newKidsPolicy = {
				hasError: false,
				isEdited: true,
				value: kidsPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							kidsMaxAge: {
								isEdited: true,
								value: item.kidsMaxAge?.value ?? 0,
							},
							kidsDiscountType: {
								isEdited: true,
								value: discountType,
							},
							ratio: {
								isEdited: true,
								value: item.ratio?.value ?? 0,
							},
							static:
								discountType === KidsPolicyType.Static
									? {
											amount: {
												isEdited: true,
												value: item.static?.amount?.value ?? 1000,
											},
											currencyType: {
												isEdited: true,
												value: item.static?.currencyType?.value ?? CurrencyType.Jpy,
											},
										}
									: undefined,
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
		},
		[kidsPolicy, onSetViewModel],
	);

	const ratioOptions = getOptionNumArray(0, 5, 21, { suffix: "％" });
	const updateRatio = useCallback(
		({ ratio, index }: { ratio: number; index: number }) => {
			const newKidsPolicy = {
				hasError: false,
				isEdited: true,
				value: kidsPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							...item,
							ratio: {
								isEdited: true,
								value: ratio,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
		},
		[kidsPolicy, onSetViewModel],
	);

	const updateStaticPrice = useCallback(
		({ staticPrice, index }: { staticPrice: number | undefined; index: number }) => {
			const newKidsPolicy = {
				hasError: true,
				isEdited: true,
				value: kidsPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							...item,
							static: {
								amount: {
									isEdited: true,
									value: staticPrice!,
								},
								currencyType: {
									isEdited: true,
									value: item.static?.currencyType?.value ?? CurrencyType.Jpy,
								},
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "kidsPolicy", value: newKidsPolicy }]);
		},
		[kidsPolicy, onSetViewModel],
	);

	return {
		kidsPolicy,
		append,
		canRemoveKidsPolicyItem,
		removeKidsPolicyByIndex,
		getMinAge,
		kidsMaxAgeOptions,
		updateMaxAge,
		discountTypeOptions,
		updateDiscountType,
		ratioOptions,
		updateRatio,
		updateStaticPrice,
	};
};
